import type {
  CodeBlockRedlineItem,
  PlaybookRuleRun,
  PlaybookRuleStatus,
  PlaybookStatus,
  ProposalDecisions,
  ProposalSuggestion,
} from '@aline/common';
import {
  getIsPlaybookRuleMidReview,
  getIsPlaybookRuleReviewable,
  getIsPlaybookRuleRunFailed,
  getIsPlaybookRuleRunFinished,
  getIsPlaybookRuleRunning,
  getPlaybookRuleStatusForRun,
  getPlaybookStatusForRuns,
  getReviewableSuggestions,
  sortPositionResultsByRank,
} from '@aline/common';
import { Apollo } from 'src/gql/generated';

export type PlaybookCategory = Apollo.PlaybookRuleCategoryWithRuleJobsFragment;
export type PlaybookRule = Apollo.PlaybookRuleWithJobFragment;
export type PlaybookRuleJob = Apollo.PlaybookRuleJobWithProposalsFragment;
export type PlaybookPositionResult = Apollo.PlaybookRuleJobPositionResultSimpleFragment;
export type PlaybookProposal = Apollo.PlaybookRuleProposalSimpleFragment;

/**
 * The job shape every status derivation reads: everything but the suggestion
 * payloads — its proposals carry decisions only. The full `PlaybookRuleJob`
 * satisfies it structurally, and so does the summary query's job, which is
 * the point: the open-playbook drawer derives status (Reviewing included)
 * without ever loading a suggestion.
 */
export type PlaybookRuleJobSummary = Apollo.PlaybookRuleJobSimpleFragment;
export type PlaybookRuleLike = { id: string; ruleJob?: PlaybookRuleJobSummary | null };
export type PlaybookCategoryLike = { id: string; rules: PlaybookRuleLike[] };

/**
 * The status types and the derivation itself live in `@aline/common`: the
 * playbook list's chip is a rollup the server computes from the same rules
 * over the same facts, so neither side owns a second copy of it.
 */
export type { PlaybookRuleStatus, PlaybookStatus };

export type PlaybookRuleFilter = 'all' | 'passed' | 'resolved' | 'review';

export interface PlaybookRuleFilterCounts {
  all: number;
  passed: number;
  resolved: number;
  review: number;
}

export function getRuleJob<TJob>(rule: { ruleJob?: TJob | null }): TJob | null {
  return rule.ruleJob ?? null;
}

export function getIsRuleRunning(job: PlaybookRuleJobSummary | null): boolean {
  return getIsPlaybookRuleRunning(getRuleRun(job));
}

export function getIsRuleRun(job: PlaybookRuleJobSummary | null): boolean {
  return getIsPlaybookRuleRunFinished(getRuleRun(job));
}

/** Whether the run itself errored, as opposed to positions that failed. */
export function getIsRuleRunFailed(job: PlaybookRuleJobSummary | null): boolean {
  return getIsPlaybookRuleRunFailed(getRuleRun(job));
}

/** Results in the order the rule read at run time (snapshotted ranks). */
export function getSortedResults(job: PlaybookRuleJobSummary | null): PlaybookPositionResult[] {
  if (!job) return [];
  return sortPositionResultsByRank(job.positionResults);
}

/**
 * The result the run passed at, if any. Evaluation stops at the first pass,
 * so this is always the last evaluated result when present.
 */
export function getPassingResult(
  job: PlaybookRuleJobSummary | null
): PlaybookPositionResult | null {
  return (
    getSortedResults(job).find(result => result.status === Apollo.PlaybookPositionStatus.Passing) ??
    null
  );
}

export function getFailingResults(job: PlaybookRuleJobSummary | null): PlaybookPositionResult[] {
  return getSortedResults(job).filter(
    result => result.status === Apollo.PlaybookPositionStatus.Failing
  );
}

/** A reviewable rule: run, with at least one failed position. */
export function getIsRuleReviewable(job: PlaybookRuleJobSummary | null): boolean {
  return getIsPlaybookRuleReviewable(getRuleRun(job));
}

export function getIsRuleResolved(job: PlaybookRuleJobSummary | null): boolean {
  return Boolean(job?.resolution);
}

/** Review underway: a decision is recorded and the rule has not resolved. */
export function getIsRuleMidReview(job: PlaybookRuleJobSummary | null): boolean {
  return getIsPlaybookRuleMidReview(getRuleRun(job));
}

export function getRuleStatus(rule: PlaybookRuleLike): PlaybookRuleStatus {
  return getPlaybookRuleStatusForRun(getRuleRun(getRuleJob(rule)));
}

/** Rules that still require a person to review or retry something. */
export function getIsRuleInReviewQueue(rule: PlaybookRuleLike): boolean {
  const status = getRuleStatus(rule);
  return status === 'failed' || status === 'review' || status === 'reviewing';
}

export function getIsRuleInFilter(rule: PlaybookRuleLike, filter: PlaybookRuleFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'review') return getIsRuleInReviewQueue(rule);
  return getRuleStatus(rule) === filter;
}

export function getRuleFilterCounts(categories: PlaybookCategoryLike[]): PlaybookRuleFilterCounts {
  const rules = categories.flatMap(category => category.rules);
  return {
    all: rules.length,
    passed: rules.filter(rule => getRuleStatus(rule) === 'passed').length,
    resolved: rules.filter(rule => getRuleStatus(rule) === 'resolved').length,
    review: rules.filter(getIsRuleInReviewQueue).length,
  };
}

/** Starting vs fallback label from order in the list being rendered. */
export function getPlaybookPositionLabel(index: number): string {
  if (index === 0) return 'Starting Position';
  return `Fallback Position ${index}`;
}

/** What a collapsed category summarizes to — first match wins (handoff §6). */
export type PlaybookCategorySummary =
  | { type: 'running' }
  | { type: 'reviewing' }
  | { type: 'review' }
  | { type: 'resolved' }
  | { type: 'passed' }
  | { count: number; type: 'count' };

/** Whether any rule of the playbook is still being evaluated against the document. */
export function getIsAnyRuleRunning(categories: Pick<PlaybookCategoryLike, 'rules'>[]): boolean {
  return categories.some(category =>
    category.rules.some(rule => getIsRuleRunning(getRuleJob(rule)))
  );
}

/**
 * Summarize the rules currently shown in a category. When an outcome filter
 * is on, pass the visible subset so hidden siblings do not leak into the chip.
 */
export function getCategorySummary(rules: PlaybookRuleLike[]): PlaybookCategorySummary {
  const jobs = rules.map(rule => getRuleJob(rule));
  if (jobs.some(getIsRuleRunning)) return { type: 'running' };
  // Work underway outranks work waiting: Reviewing before Review.
  if (jobs.some(getIsRuleMidReview)) return { type: 'reviewing' };
  if (jobs.some(job => getIsRuleReviewable(job) && !getIsRuleResolved(job)))
    return { type: 'review' };

  const allRun =
    rules.length > 0 && jobs.every(job => getIsRuleRun(job) || getIsRuleRunFailed(job));
  if (allRun) {
    return jobs.some(getIsRuleReviewable) ? { type: 'resolved' } : { type: 'passed' };
  }

  return { count: rules.length, type: 'count' };
}

/**
 * Outcome chips stay off for the not-run empty state, and stay off from the
 * playbook-wide Run/Rerun click through the last streaming result so All
 * stays locked while every count is still moving.
 *
 * Rerunning individual rules keeps the chips: a card's Rerun and the
 * automatic rerun of rules whose paragraphs changed both leave the rest of
 * the playbook settled, so the counts still read true and dropping the row
 * would jump the whole list up under the reader.
 */
export function getShowPlaybookOutcomeFilters({
  isFullRunInFlight,
  status,
}: {
  isFullRunInFlight: boolean;
  status: PlaybookStatus;
}): boolean {
  return status !== 'not-run' && !isFullRunInFlight;
}

/**
 * The playbook rollup, over the rules the caller is holding. The list card does
 * not come through here: its chip is the server's `runStatus`, which is this
 * same derivation over the same facts.
 */
export function getPlaybookStatus(categories: PlaybookCategoryLike[]): PlaybookStatus {
  const rules = categories.flatMap(category => category.rules);
  return getPlaybookStatusForRuns(rules.map(rule => getRuleRun(getRuleJob(rule))));
}

/** Rule counts behind a playbook's run, for progress bars and one-line summaries. */
export interface PlaybookRunProgress {
  completedCount: number;
  failedCount: number;
  passedCount: number;
  percent: number;
  resolvedCount: number;
  reviewCount: number;
  runningCount: number;
  status: PlaybookStatus;
  totalCount: number;
}

export function getPlaybookRunProgress(categories: PlaybookCategoryLike[]): PlaybookRunProgress {
  const rules = categories.flatMap(category => category.rules);
  const ruleStatuses = rules.map(getRuleStatus);
  const countOf = (status: PlaybookRuleStatus) =>
    ruleStatuses.filter(ruleStatus => ruleStatus === status).length;
  const totalCount = rules.length;
  const completedCount = rules.filter(rule => {
    const job = getRuleJob(rule);
    return getIsRuleRun(job) || getIsRuleRunFailed(job);
  }).length;

  return {
    completedCount,
    failedCount: countOf('failed'),
    passedCount: countOf('passed'),
    percent: totalCount ? Math.round((completedCount / totalCount) * 100) : 0,
    resolvedCount: countOf('resolved'),
    reviewCount: countOf('review') + countOf('reviewing'),
    runningCount: countOf('running'),
    status: getPlaybookStatus(categories),
    totalCount,
  };
}

export function getPlaybookRunSummary(progress: PlaybookRunProgress): string {
  const rulesRun = `${progress.completedCount}/${progress.totalCount} Rules Run`;
  if (progress.status === 'running') return rulesRun;
  if (progress.status === 'not-run') return 'Playbook has not run for this document.';

  const outcomes = [
    formatOutcome('Review', progress.reviewCount),
    formatOutcome('Passed', progress.passedCount),
    formatOutcome('Resolved', progress.resolvedCount),
    formatOutcome('Failed', progress.failedCount),
  ].filter(Boolean);

  return outcomes.length ? `${rulesRun}: ${outcomes.join(', ')}` : rulesRun;
}

function formatOutcome(label: string, count: number): string {
  if (!count) return '';
  return `${count} ${label}`;
}

/**
 * The live review queue: unresolved legal reviews plus rules that failed to
 * run and need a retry. The detail panel snapshots these ids when the filter
 * is applied so resolving a rule never removes it from under the user's eye.
 */
export function getReviewTargets(categories: PlaybookCategoryLike[]): {
  categoryIds: Set<string>;
  ruleIds: Set<string>;
} {
  const categoryIds = new Set<string>();
  const ruleIds = new Set<string>();
  for (const category of categories) {
    for (const rule of category.rules) {
      if (!getIsRuleInReviewQueue(rule)) continue;
      categoryIds.add(category.id);
      ruleIds.add(rule.id);
    }
  }
  return { categoryIds, ruleIds };
}

export function getAdjacentReviewQueueRuleId(options: {
  activeRuleId: string | null;
  direction: 'next' | 'previous';
  liveRuleIds: ReadonlySet<string>;
  snapshotRuleIds: string[];
}): string | null {
  const { activeRuleId, direction, liveRuleIds, snapshotRuleIds } = options;
  let currentIndex = snapshotRuleIds.length;
  if (activeRuleId) currentIndex = snapshotRuleIds.indexOf(activeRuleId);
  else if (direction === 'next') currentIndex = -1;

  const step = direction === 'next' ? 1 : -1;
  for (
    let index = currentIndex + step;
    index >= 0 && index < snapshotRuleIds.length;
    index += step
  ) {
    const ruleId = snapshotRuleIds[index];
    if (liveRuleIds.has(ruleId)) return ruleId;
  }
  return null;
}

/**
 * The position review has locked the rule to (ADR 0006): the one position
 * with recorded decisions on an unresolved rule. Null once the rule resolves
 * — the resolution record carries the lock from then on.
 */
export function getReviewingPositionId(
  proposals: Array<{ decisions: ProposalDecisions; positionId: string }>,
  isResolved: boolean
): string | null {
  if (isResolved) return null;
  return proposals.find(proposal => Object.keys(proposal.decisions).length > 0)?.positionId ?? null;
}

/**
 * The stored decisions overlaid with accepts still in flight. An accept lands
 * in the document first and only then records its decision, so for the round
 * trip the suggestion is undecided in the run data while its own edit has
 * already changed the document — every decision-driven derivation (staleness
 * above all: the suggestion would read as stale from its own landed edit)
 * must treat it as accepted from the moment the accept began.
 */
export function getEffectiveDecisions(
  suggestions: ProposalSuggestion[],
  decisions: ProposalDecisions,
  pendingAcceptedSuggestionIds: ReadonlySet<string>
): ProposalDecisions {
  const effective: ProposalDecisions = { ...decisions };
  for (const suggestion of suggestions) {
    if (pendingAcceptedSuggestionIds.has(suggestion.id) && effective[suggestion.id] === undefined) {
      effective[suggestion.id] = 'accepted';
    }
  }
  return effective;
}

/**
 * Whether a position's proposal has gone stale against the live document.
 * Judged over the undecided reviewable suggestions only: decided ones are
 * settled, and an accept always stales its own paragraph — counting it would
 * surface the rerun banner the moment any suggestion was accepted. And only
 * while the rule is unresolved: the resolving action's own edits are what
 * made things hash-stale, which is expected, not an error.
 */
export function getIsPositionProposalStale(options: {
  decisions: ProposalDecisions;
  getIsStale: (previewCards: CodeBlockRedlineItem[]) => boolean;
  isResolved: boolean;
  suggestions: ProposalSuggestion[];
}): boolean {
  const { decisions, getIsStale, isResolved, suggestions } = options;
  if (isResolved) return false;
  return getReviewableSuggestions(suggestions)
    .filter(suggestion => decisions[suggestion.id] === undefined)
    .some(suggestion => getIsStale(suggestion.previewCards));
}

export interface PositionActionLocks {
  /** Bulk accept: frozen by others' resolutions, sibling review, staleness. */
  acceptRemainingDisabled: boolean;
  /** The per-suggestion accept/reject/undo controls. */
  reviewControlsDisabled: boolean;
}

/**
 * When a failing position's actions disable (handoff §7 + ADR 0006):
 *
 * - Any resolution disables everything except the button that made it (its
 *   active state stays clickable to clear).
 * - Review locks the rule to one position: a sibling mid-review disables all
 *   of this position's actions.
 * - Staleness freezes accept-remaining while the per-suggestion controls stay
 *   live for rejects.
 * - Accept-remaining also needs something left to accept.
 */
export function getPositionActionLocks(options: {
  hasResolution: boolean;
  hasUndecided: boolean;
  isProposalStale: boolean;
  isSiblingReviewing: boolean;
  reviewedActive: boolean;
}): PositionActionLocks {
  const { hasResolution, hasUndecided, isProposalStale, isSiblingReviewing, reviewedActive } =
    options;

  return {
    acceptRemainingDisabled:
      (hasResolution && !reviewedActive) ||
      isSiblingReviewing ||
      (!reviewedActive && (isProposalStale || !hasUndecided)),
    reviewControlsDisabled: hasResolution || isSiblingReviewing,
  };
}

/**
 * The run query's rule job as the facts every status derivation reads. Null
 * for a rule the document has no run for, which is what keeps a part-finished
 * playbook out of Passed.
 */
function getRuleRun(job: PlaybookRuleJobSummary | null): PlaybookRuleRun | null {
  if (!job) return null;

  return {
    failedAt: job.failedAt,
    finishedAt: job.finishedAt,
    hasFailingPosition: getFailingResults(job).length > 0,
    hasReviewDecision: job.proposals.some(proposal => Object.keys(proposal.decisions).length > 0),
    isResolved: getIsRuleResolved(job),
    startedAt: job.startedAt,
  };
}
